// Vederea mare a unui setup: graficul cu urmele jucătorului mare și tot ce trebuie ca
// să decizi, pe un singur ecran.
//
// Stânga: graficul viu, cu planul (nivel, intrare, stop, țintă) și stratul „Крупный
// игрок" (plotnosti de pe 4 burse, ordine mari, absorbție, profil de volum).
// Dreapta: planul, fluxul, ce face jucătorul mare chiar acum spus în cuvinte, legenda
// cu fiecare culoare explicată (bifele ascund straturile) și „Разбор сетапа" — analiza
// scrisă de algoritm, refăcută la fiecare citire nouă a datelor.
//
// Regula legendei: fiecare lucru desenat are un rând în ea, iar culorile vin din
// același obiect ca desenul (`LEGEND` din chart/whales.js) — nu pot să se despartă.

import { el, clear, fmtPrice } from '../core/util.js';
import { chartTheme } from '../core/theme.js';
import { Chart } from '../chart/chart.js';
import {
  WhaleFeed, WhaleLayer, LEGEND, planLegendFor, money, varsta,
} from '../chart/whales.js';
import { analyzeSetup } from './setup-analysis.js';
import { renderPlan, renderFlow, drawPlanLines, coinButtons } from './setup-parts.js';
import { iconMoneda } from './coin-icon.js';
import { icon } from './icons.js';
import { baseTicker, tigerBtn } from './components.js';

const GRUPE = [
  { id: 'walls', title: 'Плотности в стаканах 4 бирж' },
  { id: 'prints', title: 'Крупные сделки по рынку' },
  { id: 'absorb', title: 'Поглощение' },
  { id: 'profile', title: 'Профиль объёма' },
];
const NUME = { binance: 'Binance', bybit: 'Bybit', okx: 'OKX', bitget: 'Bitget' };
const KEY_SHOW = 'sx_setup_view_layers';

function swatch(l) {
  // Prefixul `sw-` nu e decor: `.dash` simplu e deja clasa panoului de start.
  const s = el('span', { class: `sv-sw sw-${l.shape}` });
  // Proprietățile CSS proprii nu trec prin `Object.assign(style)`; doar prin setProperty.
  s.style.setProperty('--c', l.color);
  return s;
}

export function openSetupView(item, { app, tigerExchange, latest, history, chartExchange } = {}) {
  const sursa = chartExchange || item.exchange;
  let curent = item;
  let show = { walls: true, prints: true, absorb: true, profile: true };
  try { show = { ...show, ...JSON.parse(localStorage.getItem(KEY_SHOW) || '{}') }; } catch { /* implicit */ }

  const chartHost = el('div', { class: 'sv-chart' });
  const planEl = el('div', { class: 'form-plan' });
  const fluxEl = el('div', { class: 'form-flow' });
  const acumEl = el('div', { class: 'sv-now' });
  const legendEl = el('div', { class: 'sv-legend' });
  const analizaEl = el('div', { class: 'sv-ai-out' });
  const tfBox = el('div', { class: 'sv-tfs' });
  // Banda „din istorie": apare când formația nu mai e în listă — momentul ei a trecut,
  // dar tranzacția după plan poate merge mai departe; banda spune unde a ajuns.
  const bandaEl = el('div', { class: 'sv-history' });
  bandaEl.hidden = true;
  // O apropiere are patru linii la jumătate de ATR de nivel: pe 5m se lipesc într-o
  // bandă. Pe 1m se văd separat — și exact acolo se decide intrarea.
  let tf = item.kind === 'approach' ? '1m' : (item.tf || '5m');

  const lung = item.direction === 'long';
  const g = item.grade || {};
  const inchide = () => {
    feed.stop();
    chart.destroy();
    clearInterval(timer);
    overlay.remove();
    window.removeEventListener('keydown', esc);
  };
  const esc = (e) => { if (e.key === 'Escape') inchide(); };
  window.addEventListener('keydown', esc);

  const overlay = el('div', { class: 'sv-overlay', onclick: (e) => { if (e.target === overlay) inchide(); } }, [
    el('div', { class: 'sv' }, [
      el('div', { class: 'sv-head' }, [
        item.kind === 'approach'
          ? el('span', { class: 'fs-badge', text: 'СКОРО' })
          : el('span', { class: `form-grade g${g.grade || 'C'}`, text: g.grade || '—' }),
        ...coinButtons(item, () => { inchide(); app?.openInstrument(item); }, 18),
        tigerBtn(item.symbol, tigerExchange || item.exchange, item.market),
        item.kind === 'approach' ? null
          : el('span', { class: `idea-dir ${lung ? 'long' : 'short'}`, text: lung ? 'LONG' : 'SHORT' }),
        el('span', { class: 'form-label', text: item.label }),
        tfBox,
        el('span', { class: 'sv-src', text: `свечи: ${sursa} · стаканы и сделки: 4 биржи` }),
        el('button', { class: 'modal-close', title: 'Закрыть (Esc)', onclick: inchide }, [icon('close', 16)]),
        bandaEl,
      ]),
      el('div', { class: 'sv-body' }, [
        chartHost,
        el('aside', { class: 'sv-side' }, [
          el('section', { class: 'sv-ai' }, [el('h4', { text: 'Разбор сетапа' }), analizaEl]),
          el('section', {}, [el('h4', { text: 'План' }), planEl]),
          el('section', {}, [fluxEl]),
          el('section', {}, [el('h4', { text: 'Крупный игрок сейчас' }), acumEl]),
          el('section', {}, [el('h4', { text: 'Что означают линии и цвета' }), legendEl]),
        ]),
      ]),
    ]),
  ]);
  document.body.append(overlay);

  // ------------------------------------------------------------- grafic

  const chart = new Chart(chartHost, {
    exchange: sursa, market: item.market, symbol: item.symbol, tf,
    compact: false, limit: 600, indicators: { volume: true }, showLevels: true,
    onBar: (bar) => feed.notePrice(bar.close),
  });
  const feed = new WhaleFeed(item.symbol, {
    quoteVolume: item.quoteVolume || 0,
    onUpdate: () => { layer?.render(); renderNow(); },
  });
  let layer = null;
  let linii = [];
  const deseneazaPlan = () => {
    // Pe graficul mare liniile planului sunt groase: la 1 px se pierdeau printre niveluri.
    linii = drawPlanLines(chart, curent, chartTheme().levelLine, linii, { width: 2 });
  };
  chart.load().then(() => {
    layer = chart.addLayer(new WhaleLayer(chart, feed));
    for (const [k, v] of Object.entries(show)) layer.setVisible(k, v);
    deseneazaPlan();
  }).catch(() => chartHost.append(el('div', { class: 'card-error', text: 'Не удалось загрузить свечи' })));
  feed.start();

  for (const t of ['1m', '5m', '15m', '1h']) {
    tfBox.append(el('button', {
      class: `sv-tf${t === tf ? ' active' : ''}`, text: t,
      onclick: async () => {
        tf = t;
        for (const b of tfBox.children) b.classList.toggle('active', b.textContent === t);
        await chart.setTimeframe(t);
        deseneazaPlan();
        layer?.render();
      },
    }));
  }

  // --------------------------------------------------------- panoul drept

  const renderSide = () => {
    renderPlan(planEl, curent);
    renderFlow(fluxEl, feed.flow || curent.flow, curent.direction);
  };

  /** Ce face jucătorul mare, în propoziții — legenda spune ce e, asta spune ce se întâmplă. */
  function renderNow() {
    renderSide();
    renderAnaliza();
    clear(acumEl);
    const scrise = new Set();
    const rand = (cls, text) => {
      if (scrise.has(text)) return;      // același zid citit de două ori nu e două ziduri
      scrise.add(text);
      acumEl.append(el('div', { class: `sv-now-row ${cls}`, text }));
    };
    const pret = feed.lastPrice;
    const walls = feed.walls.filter((w) => !w.gone);
    const aproape = (side) => walls
      .filter((w) => w.side === side)
      .sort((a, b) => Math.abs(a.price - pret) - Math.abs(b.price - pret))[0];
    for (const side of ['ask', 'bid']) {
      const w = pret && aproape(side);
      if (!w) continue;
      const dist = ((w.price - pret) / pret) * 100;
      const stare = w.state === 'eating' ? ' — съедается сейчас' : '';
      rand(side === 'bid' ? 'bid' : 'ask',
        `${side === 'bid' ? 'Плотность на покупку' : 'Плотность на продажу'} ${money(w.usd)} на ${fmtPrice(w.price)} `
        + `(${dist >= 0 ? '+' : ''}${dist.toFixed(2)}%), ${NUME[w.ex]}, стоит ${varsta(w.age)}${stare}`);
    }
    for (const w of feed.walls.filter((x) => x.gone).slice(0, 3)) {
      rand(w.state === 'eaten' ? 'eaten' : 'pulled',
        `${w.state === 'eaten' ? '✕ Съедена' : '⌀ Снята'} ${w.side === 'bid' ? 'покупка' : 'продажа'} `
        + `${money(w.peak)} на ${fmtPrice(w.price)} (${NUME[w.ex]})`);
    }
    for (const a of feed.absorption.slice(0, 3)) {
      rand('absorb', `${a.kind === 'absorb_sell' ? 'Поглощение продаж' : 'Поглощение покупок'} `
        + `${money(a.usd)} на ${fmtPrice(a.price)}, держится ${a.held} мин`);
    }
    const ultimele = feed.prints.slice(-60);
    if (ultimele.length) {
      const c = ultimele.filter((p) => p.side === 'buy').reduce((s, p) => s + p.usd, 0);
      const v = ultimele.filter((p) => p.side === 'sell').reduce((s, p) => s + p.usd, 0);
      rand(c >= v ? 'buy' : 'sell',
        `Крупные сделки (от ${money(feed.threshold || 0)}): покупки ${money(c)}, продажи ${money(v)}`);
    }
    if (!acumEl.children.length) {
      rand('mute', feed.watching === false
        ? 'Монета не торгуется на фьючерсах этих бирж.'
        : 'Собираю стаканы и сделки 4 бирж… Плотности появятся через 15–20 секунд, крупные сделки — по мере торгов.');
    }
  }

  function renderLegend() {
    clear(legendEl);
    for (const grup of GRUPE) {
      const bifa = el('input', {
        type: 'checkbox', checked: show[grup.id],
        onchange: (e) => {
          show[grup.id] = e.target.checked;
          try { localStorage.setItem(KEY_SHOW, JSON.stringify(show)); } catch { /* doar pentru sesiunea asta */ }
          layer?.setVisible(grup.id, show[grup.id]);
        },
      });
      legendEl.append(el('label', { class: 'sv-lg-group' }, [bifa, el('span', { text: grup.title })]));
      for (const l of LEGEND.filter((x) => x.group === grup.id)) {
        legendEl.append(el('div', { class: 'sv-lg-row' }, [
          swatch(l), el('div', {}, [el('b', { text: l.title }), el('span', { text: l.text })]),
        ]));
      }
    }
    legendEl.append(el('div', { class: 'sv-lg-group static', text: 'План и уровни' }));
    for (const l of planLegendFor(item.kind)) {
      legendEl.append(el('div', { class: 'sv-lg-row' }, [
        swatch(l), el('div', {}, [el('b', { text: l.title }), el('span', { text: l.text })]),
      ]));
    }
  }

  /** Analiza algoritmică, în aceleași patru secțiuni ca un trader care gândește cu voce tare. */
  function renderAnaliza() {
    const a = analyzeSetup(curent, feed);
    clear(analizaEl);
    analizaEl.append(el('div', { class: `sv-verdict ${a.verdict.cls}`, text: a.verdict.text }));
    const bloc = (titlu, randuri) => {
      if (!randuri.length) return;
      analizaEl.append(el('div', { class: 'sv-an-block' }, [
        el('b', { text: titlu }),
        ...randuri.map((r) => el('p', { text: r })),
      ]));
    };
    bloc('Что происходит', a.now);
    bloc('Что подтвердит', a.confirm);
    bloc('Где ошибка', a.invalid);
  }

  // Formația se reîmprospătează în fila de dedesubt; vederea mare ia ultima versiune.
  const actualizeazaBanda = () => {
    const h = history?.();
    bandaEl.hidden = !h;
    if (h) {
      bandaEl.className = `sv-history ${h.status || ''}`;
      bandaEl.textContent = h.text;
    }
  };
  const timer = setInterval(() => {
    actualizeazaBanda();
    const nou = latest?.();
    if (nou && nou !== curent) {
      curent = nou;
      deseneazaPlan();
      renderSide();
    }
  }, 5000);

  renderLegend();
  renderNow();
  actualizeazaBanda();
}
